import React, { useState, useEffect, MouseEvent } from 'react';
import {
  AppBar as MuiAppBar,
  Toolbar,
  IconButton,
  Box,
  Tooltip,
  Menu,
  MenuItem,
  ListItemIcon,
  Divider,
  Typography,
  Select,
  FormControl,
  InputLabel,
  SelectChangeEvent,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import AccountCircle from '@mui/icons-material/AccountCircle';
import PersonIcon from '@mui/icons-material/Person';
import LogoutIcon from '@mui/icons-material/Logout';
import BusinessIcon from '@mui/icons-material/Business';

import logo from '../../assets/img/logo_v3.png';
import useAuth from '../../hooks/useAuth';
import { useNavigate } from 'react-router-dom';
import { useEntity } from '../../context/EntityContext';

interface AppBarProps {
  onMenuClick: () => void;
}

const AppBar: React.FC<AppBarProps> = ({ onMenuClick }) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [entityAnchorEl, setEntityAnchorEl] = useState<null | HTMLElement>(null);

  const { user, signOut } = useAuth();
  const { selectedEntity, setSelectedEntity } = useEntity();

  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  useEffect(() => {
    if (
      !selectedEntity &&
      user?.entities &&
      user.entities.length > 0
    ) {
      setSelectedEntity(user.entities[0].id);
    }
  }, [selectedEntity, user?.entities, setSelectedEntity]);  

  const handleMenuOpen = (event: MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleEntityMenuOpen = (event: MouseEvent<HTMLElement>) => {
    setEntityAnchorEl(event.currentTarget);
  };

  const handleEntityMenuClose = () => {
    setEntityAnchorEl(null);
  };

  const handleProfileClick = () => {
    const env = process.env.NODE_ENV;
    let targetUrl = '';
  
    if (env === 'development') {
      targetUrl = `http://localhost:3000`;
    } else {
      targetUrl = `https://connect.publisol.com.br`;
    }
  
    window.open(targetUrl, '_blank');
  };

  const handleSignOut = () => {
    handleMenuClose();
    signOut();
    navigate('/login');
  };

  const handleEntityChange = (event: SelectChangeEvent<string>) => {
    setSelectedEntity(event.target.value as string);
    handleEntityMenuClose();
  };

  const firstName = user?.full_name?.split(' ')[0] || 'Usuário';

  return (
    <MuiAppBar position="static">
      <Toolbar>
        <Tooltip title="Menu e suas opções">
          <IconButton
            edge="start"
            color="inherit"
            aria-label="open drawer"
            onClick={onMenuClick}
          >
            <MenuIcon />
          </IconButton>
        </Tooltip>
        {!isMobile && (
          <Typography sx={{ ml: 2 }}>
            Bem vindo, {firstName}!
          </Typography>
        )}

        <Box
          sx={{
            flexGrow: 1,
            display: 'flex',
            justifyContent: 'center',
            position: 'absolute',
            left: '50%',
            transform: 'translateX(-50%)',
          }}
        >
          <Tooltip title="Publisol Acta | Diário Oficial Inteligente">
            <img src={logo} alt="Publisol Acta Logo" style={{ height: 54 }} />
          </Tooltip>
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'center', marginLeft: 'auto' }}>
          {isMobile && (user?.entities?.length ?? 0) > 0 && (
            <Tooltip title="Selecionar Entidade">
              <IconButton
                sx={{ p: '10px' }}
                aria-label="select entity"
                color="inherit"
                onClick={handleEntityMenuOpen}
              >
                <BusinessIcon />
              </IconButton>
            </Tooltip>
          )}
          {!isMobile && (user?.entities?.length ?? 0) > 0 && (
            <FormControl sx={{ mr: 2, minWidth: 120 }} variant="outlined" size="small">
              <InputLabel id="entity-select-label" sx={{ color: 'white' }}>
                Entidade
              </InputLabel>
              <Select
                labelId="entity-select-label"
                id="entity-select"
                value={selectedEntity || ''}
                onChange={handleEntityChange}
                label="Entidade"
                sx={{
                  color: 'white',
                  '& .MuiOutlinedInput-notchedOutline': {
                    borderColor: 'white',
                  },
                  '&:hover .MuiOutlinedInput-notchedOutline': {
                    borderColor: 'white',
                  },
                  '& .MuiSvgIcon-root': {
                    color: 'white',
                  },
                }}
              >
                {user?.entities?.map((ent) => (
                  <MenuItem key={ent.id} value={ent.id}>
                    {ent.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}

          <Tooltip title="Opções do usuário.">
            <IconButton
              edge="end"
              aria-label="account of current user"
              aria-controls="primary-search-account-menu"
              aria-haspopup="true"
              color="inherit"
              onClick={handleMenuOpen}
            >
              <AccountCircle />
            </IconButton>
          </Tooltip>

          {/* SELECT no modo mobile dentro de um Menu */}
          <Menu
            id="entity-select-menu"
            anchorEl={entityAnchorEl}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            keepMounted
            transformOrigin={{ vertical: 'top', horizontal: 'right' }}
            open={Boolean(entityAnchorEl)}
            onClose={handleEntityMenuClose}
          >
            {(user?.entities?.length ?? 0) > 0 && (
              <FormControl sx={{ m: 2, minWidth: 120 }} variant="outlined" size="small">
                <InputLabel id="mobile-entity-select-label">Entidade</InputLabel>
                <Select
                  labelId="mobile-entity-select-label"
                  id="mobile-entity-select"
                  value={selectedEntity || ''}
                  onChange={handleEntityChange}
                  label="Entidade"
                >
                  {user?.entities?.map((ent) => (
                    <MenuItem key={ent.id} value={ent.id}>
                      {ent.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}
          </Menu>

          <Menu
            id="primary-search-account-menu"
            anchorEl={anchorEl}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            keepMounted
            transformOrigin={{ vertical: 'top', horizontal: 'right' }}
            open={Boolean(anchorEl)}
            onClose={handleMenuClose}
            slotProps={{
              paper: {
                elevation: 3,
                sx: {
                  mt: 1.5,
                  minWidth: 200,
                },
              },
            }}
          >
            <Tooltip title="Gerenciar suas informações.">
              <MenuItem onClick={handleProfileClick} sx={{ fontSize: '0.875rem' }}>
                <ListItemIcon>
                  <PersonIcon fontSize="small" />
                </ListItemIcon>
                Meu Perfil
              </MenuItem>
            </Tooltip>
            <Divider />
            <Tooltip title="Desconectar sua conta Publisol.">
              <MenuItem onClick={handleSignOut} sx={{ fontSize: '0.875rem' }}>
                <ListItemIcon>
                  <LogoutIcon fontSize="small" />
                </ListItemIcon>
                Desconectar
              </MenuItem>
            </Tooltip>
          </Menu>
        </Box>
      </Toolbar>
    </MuiAppBar>
  );
};

export default AppBar;